import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/browser';

import { useDynamicMethods, useVoip } from '$app-web/components/chat/state/chat-client.state';

import { CALL_TYPES, CallType } from './types';

export interface IncomingCallPayload {
    callId: string;
    callerName: string;
    callType: CallType;
}

type ReportIncomingCall = (payload: IncomingCallPayload) => void;
type ReportCallEnded = (callId: string) => void;

interface VoipState {
    incomingCallsData?: Array<{ id: string; roomId: string; type: string }>;
}

interface DynamicMethods {
    getRoomTitle: (roomId: string) => string | undefined;
}

export function useIncomingCallReporter(report: ReportIncomingCall, reportEnded: ReportCallEnded) {
    const voip = useVoip() as VoipState;
    const dynamicMethods = useDynamicMethods() as DynamicMethods;
    const lastReportedCallIdRef = useRef<string | null>(null);
    const failedCallIdRef = useRef<string | null>(null);

    const currentCall = voip?.incomingCallsData?.[0] ?? null;
    const currentCallId = currentCall?.id ?? null;
    const currentCallRoomId = currentCall?.roomId;
    const currentCallType = currentCall?.type;

    useEffect(() => {
        if (currentCallId === null) {
            if (lastReportedCallIdRef.current !== null) {
                reportEnded(lastReportedCallIdRef.current);
                lastReportedCallIdRef.current = null;
            }
            failedCallIdRef.current = null;
            return;
        }

        if (lastReportedCallIdRef.current === currentCallId) {
            return;
        }

        // Already attempted and failed for this call — don't retry every render.
        if (failedCallIdRef.current === currentCallId) {
            return;
        }

        // Previous call ended, a new one took its place: notify ended for the old one first.
        if (lastReportedCallIdRef.current !== null) {
            reportEnded(lastReportedCallIdRef.current);
            lastReportedCallIdRef.current = null;
        }

        // Only mark as reported AFTER report() returns successfully; otherwise a thrown
        // report could leave the ref pointing at a call the parent never received,
        // causing a phantom _ENDED later.
        try {
            report({
                callId: currentCallId,
                callerName: (currentCallRoomId && dynamicMethods.getRoomTitle(currentCallRoomId)) ?? '',
                callType: currentCallType === CALL_TYPES.VIDEO ? CALL_TYPES.VIDEO : CALL_TYPES.VOICE,
            });
            lastReportedCallIdRef.current = currentCallId;
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setTag('catch_in', 'useIncomingCallReporter');
                scope.setExtras({ callId: currentCallId, roomId: currentCallRoomId, callType: currentCallType });
                Sentry.captureException(error);
            });
            failedCallIdRef.current = currentCallId;
        }
    }, [currentCallId, currentCallRoomId, currentCallType, dynamicMethods, report, reportEnded]);
}
